import {
  Box,
  Button,
  HStack,
  Input,
  SimpleGrid,
  Text,
  VStack
} from "@chakra-ui/react"
import React, { useCallback, useState } from "react"
import MultiLocationPicker, { LocationEntry, newLocation } from "./MultiLocationPicker"

const BUILT_FORMS = ["Detached", "Semi-Detached", "Mid-Terrace", "End-Terrace"]
const EPC_BANDS = ["A", "B", "C", "D", "E", "F", "G"]

export interface PortfolioFormData {
  name: string
  locations: LocationEntry[]
  built_forms: string[]
  min_bedrooms: number | null
  max_bedrooms: number | null
  max_asking_price: string | null
  min_epc_band: string
}

interface Props {
  onSubmit: (data: PortfolioFormData) => Promise<void>
  isSubmitting: boolean
  submitLabel?: string
  /** When true, only the portfolio name field is shown (no criteria) */
  nameOnly?: boolean
}

const PortfolioForm: React.FC<Props> = ({
  onSubmit,
  isSubmitting,
  submitLabel = "Save →",
  nameOnly = false
}) => {
  const [portfolioName, setPortfolioName] = useState("")
  const [locations, setLocations] = useState<LocationEntry[]>([newLocation()])
  const [builtForms, setBuiltForms] = useState<string[]>([...BUILT_FORMS])
  const [minBedrooms, setMinBedrooms] = useState("")
  const [maxBedrooms, setMaxBedrooms] = useState("")
  const [maxAskingPrice, setMaxAskingPrice] = useState("")
  const [minEpcBand, setMinEpcBand] = useState("")
  const [locationError, setLocationError] = useState(false)

  const handleLocationsChange = useCallback((locs: LocationEntry[]) => {
    setLocations(locs)
  }, [])

  const toggleBuiltForm = (value: string) => {
    setBuiltForms((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    )
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!portfolioName.trim()) return

    const validLocations = locations.filter((l) => l.location_lat != null && l.location_lng != null)
    if (!nameOnly && validLocations.length === 0) {
      setLocationError(true)
      return
    }
    setLocationError(false)

    onSubmit({
      name: portfolioName.trim(),
      locations: validLocations.map((l) => ({
        location_name: l.location_name,
        location_lat: l.location_lat,
        location_lng: l.location_lng,
        radius_miles: l.radius_miles,
      })),
      built_forms: builtForms,
      min_bedrooms: minBedrooms !== "" ? Number(minBedrooms) : null,
      max_bedrooms: maxBedrooms !== "" ? Number(maxBedrooms) : null,
      max_asking_price: maxAskingPrice !== "" ? maxAskingPrice : null,
      min_epc_band: minEpcBand,
    })
  }

  return (
    <form onSubmit={handleSubmit}>
      <VStack gap={5} align="stretch" maxW="900px">
        {/* Portfolio name */}
        <Box>
          <Text fontWeight="medium" mb={1}>
            Name your portfolio
          </Text>
          <Input
            value={portfolioName}
            onChange={(e) => setPortfolioName(e.target.value)}
            placeholder="My first portfolio"
            maxW="380px"
            size="md"
            required
          />
          <Text fontSize="xs" color="gray.400" mt={1}>
            &nbsp; e.g. "South London", "ESG Portfolio", "North West Acquisitions"
          </Text>
        </Box>

        {!nameOnly && (
          <>
            <Box>
              <Box h="1px" bg="gray.200" my={2} />
              <Text fontWeight="medium" mb={0}>
                Investment criteria
              </Text>
              <Text fontSize="sm" color="gray.500">
                Greenflip matches market listings against these criteria and alerts
                you to new opportunities.
              </Text>
            </Box>

            {/* Target locations */}
            <Box>
              <Text fontWeight="medium" mb={3}>
                Target locations
              </Text>
              <MultiLocationPicker
                locations={locations}
                onChange={(locs) => { handleLocationsChange(locs); setLocationError(false) }}
                mapHeight="280px"
              />
              {locationError && (
                <Text fontSize="sm" color="red.500" mt={2}>
                  Please add at least one location before continuing.
                </Text>
              )}
            </Box>

            {/* Built form */}
            <Box>
              <Text fontWeight="medium" mb={2}>
                Property type
              </Text>
              <HStack gap={2} wrap="wrap">
                {BUILT_FORMS.map((bf) => {
                  const selected = builtForms.includes(bf)
                  return (
                    <Box
                      key={bf}
                      as="button"
                      type="button"
                      onClick={() => toggleBuiltForm(bf)}
                      px={3}
                      py={1}
                      borderRadius="full"
                      border="1px solid"
                      borderColor={selected ? "green.600" : "gray.300"}
                      bg={selected ? "green.50" : "white"}
                      color={selected ? "green.800" : "gray.600"}
                      fontWeight={selected ? "semibold" : "normal"}
                      fontSize="sm"
                      cursor="pointer"
                      transition="all 0.15s"
                      _hover={{ borderColor: "green.400", color: "green.700" }}
                    >
                      {bf}
                    </Box>
                  )
                })}
              </HStack>
            </Box>

            {/* Bedrooms + Max price */}
            <SimpleGrid columns={2} gap={6}>
              <Box>
                <Text fontWeight="medium" mb={2}>
                  Bedrooms
                </Text>
                <HStack gap={2} align="center">
                  <Input
                    type="number"
                    min={0}
                    value={minBedrooms}
                    onChange={(e) => setMinBedrooms(e.target.value)}
                    placeholder="Min"
                    w="72px"
                    size="sm"
                    textAlign="center"
                  />
                  <Text color="gray.400" fontSize="xs">to</Text>
                  <Input
                    type="number"
                    min={0}
                    value={maxBedrooms}
                    onChange={(e) => setMaxBedrooms(e.target.value)}
                    placeholder="Max"
                    w="72px"
                    size="sm"
                    textAlign="center"
                  />
                </HStack>
              </Box>

              <Box>
                <Text fontWeight="medium" mb={2}>
                  Max asking price
                </Text>
                <HStack gap={0} maxW="180px">
                  <Box
                    px={3}
                    h="36px"
                    display="flex"
                    alignItems="center"
                    bg="gray.100"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRightWidth={0}
                    borderLeftRadius="md"
                    fontSize="sm"
                    color="gray.500"
                  >
                    £
                  </Box>
                  <Input
                    type="number"
                    min={0}
                    value={maxAskingPrice}
                    onChange={(e) => setMaxAskingPrice(e.target.value)}
                    placeholder="Any"
                    size="sm"
                    borderLeftRadius={0}
                  />
                </HStack>
              </Box>
            </SimpleGrid>

            {/* EPC band */}
            <Box>
              <Text fontWeight="medium" mb={2}>
                Minimum EPC rating
              </Text>
              <HStack gap={1.5} wrap="wrap">
                {["", ...EPC_BANDS].map((b) => {
                  const selected = minEpcBand === b
                  return (
                    <Box
                      key={b || "any"}
                      as="button"
                      type="button"
                      onClick={() => setMinEpcBand(b)}
                      px={3}
                      py={1}
                      borderRadius="full"
                      border="1px solid"
                      borderColor={selected ? "green.600" : "gray.300"}
                      bg={selected ? "green.50" : "white"}
                      color={selected ? "green.800" : "gray.600"}
                      fontWeight={selected ? "semibold" : "normal"}
                      fontSize="sm"
                      cursor="pointer"
                      transition="all 0.15s"
                      _hover={{ borderColor: "green.400", color: "green.700" }}
                      minW="40px"
                      textAlign="center"
                    >
                      {b || "Any"}
                    </Box>
                  )
                })}
              </HStack>
            </Box>
          </>
        )}

        <Box>
          <Button
            type="submit"
            bg="green.800"
            color="white"
            _hover={{ bg: "green.900" }}
            size="lg"
            px={8}
            loading={isSubmitting}
            disabled={!portfolioName.trim()}
          >
            {submitLabel}
          </Button>
        </Box>
      </VStack>
    </form>
  )
}

export default PortfolioForm
